import { ExternalLink, Building2, DollarSign } from "lucide-react";
import { PlatformBadge } from "./PlatformBadge";
import { EngagementMetrics } from "./EngagementMetrics";
import type { CommunityFeedItem } from "@/hooks/useCommunityFeed";

const serif = "'Instrument Serif', Georgia, 'Times New Roman', serif";
const sans = "'Instrument Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

function timeAgo(dateStr: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatCurrency(n: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
}

interface StoryCardProps {
  item: CommunityFeedItem;
  isDark?: boolean;
}

export function StoryCard({ item, isDark = false }: StoryCardProps) {
  const isSocial = item.type === 'social';
  const isPinned = item.is_pinned;
  const isFeatured = item.is_featured;

  const cardBg = isDark ? '#152240' : '#FFFFFF';
  const textColor = isDark ? '#FAF9F6' : '#1B1B1B';
  const textMuted = isDark ? 'rgba(250,249,246,0.45)' : 'rgba(27,27,27,0.45)';
  const textFaint = isDark ? 'rgba(250,249,246,0.35)' : 'rgba(27,27,27,0.40)';
  const borderColor = isDark ? 'rgba(250,249,246,0.10)' : 'rgba(27,27,27,0.10)';
  const blue = isDark ? '#5B9BD5' : '#002868';
  const mediaBg = isDark ? 'rgba(250,249,246,0.04)' : 'rgba(27,27,27,0.04)';
  const avatarBorder = isDark ? 'rgba(250,249,246,0.12)' : 'rgba(27,27,27,0.08)';
  const footerBorder = isDark ? 'rgba(250,249,246,0.06)' : 'rgba(27,27,27,0.06)';

  return (
    <article
      className="rounded-xl overflow-hidden transition-shadow hover:shadow-lg"
      style={{
        background: cardBg,
        border: isPinned ? '2px solid #B22234' : isFeatured ? '2px solid #C5A55A' : `1px solid ${borderColor}`,
        fontFamily: sans,
      }}
    >
      {/* Pinned/Featured badge */}
      {(isPinned || isFeatured) && (
        <div
          className="px-4 py-1.5 text-xs font-semibold tracking-wide uppercase text-white"
          style={{ background: isPinned ? '#B22234' : '#C5A55A' }}
        >
          {isPinned ? 'Pinned' : 'Featured'}
        </div>
      )}

      <div className="p-5">
        {/* Header: avatar + author + platform + time */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-3">
            {item.author_avatar_url ? (
              <img
                src={item.author_avatar_url}
                alt=""
                className="w-10 h-10 rounded-full object-cover"
                style={{ border: `2px solid ${avatarBorder}` }}
              />
            ) : (
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm"
                style={{ background: isDark ? '#5B9BD5' : '#002868' }}
              >
                {(item.author_display_name || item.author_handle || '?')[0].toUpperCase()}
              </div>
            )}
            <div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-sm" style={{ color: textColor }}>
                  {item.author_display_name || item.author_handle || 'Anonymous'}
                </span>
                {item.platform && <PlatformBadge platform={item.platform} />}
              </div>
              {item.author_handle && isSocial && (
                <span className="text-xs" style={{ color: textMuted }}>
                  @{item.author_handle}
                </span>
              )}
            </div>
          </div>
          <span className="text-xs whitespace-nowrap" style={{ color: textFaint }}>
            {timeAgo(item.posted_at)}
          </span>
        </div>

        {/* Title (direct stories only) */}
        {item.title && (
          <h3 className="text-lg font-bold mb-2" style={{ fontFamily: serif, color: textColor }}>
            {item.title}
          </h3>
        )}

        {/* Hospital + pricing info (direct stories) */}
        {item.hospital_name && (
          <div className="flex items-center gap-2 mb-2 text-sm" style={{ color: blue }}>
            <Building2 className="w-4 h-4" />
            <span className="font-medium">{item.hospital_name}</span>
          </div>
        )}
        {(item.amount_charged != null || item.amount_expected != null) && (
          <div className="flex items-center gap-4 mb-3 text-sm">
            {item.amount_charged != null && (
              <span className="flex items-center gap-1 font-semibold" style={{ color: '#B22234' }}>
                <DollarSign className="w-3.5 h-3.5" />
                Charged: {formatCurrency(item.amount_charged)}
              </span>
            )}
            {item.amount_expected != null && (
              <span className="flex items-center gap-1" style={{ color: isDark ? '#6db88a' : '#2b5e49' }}>
                Expected: {formatCurrency(item.amount_expected)}
              </span>
            )}
          </div>
        )}

        {/* Post text */}
        <p className="text-sm leading-relaxed mb-3" style={{ color: textColor }}>
          {item.text.length > 400 ? `${item.text.slice(0, 400)}...` : item.text}
        </p>

        {/* Media gallery */}
        {item.media.length > 0 && (
          <div className={`mb-3 grid gap-2 ${item.media.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
            {item.media.slice(0, 4).map((m) => (
              <div key={m.id} className="rounded-lg overflow-hidden" style={{ background: mediaBg }}>
                {m.media_type === 'video' ? (
                  <video
                    src={m.r2_url || m.original_url || ''}
                    controls
                    className="w-full aspect-video object-cover"
                  />
                ) : (
                  <img
                    src={m.r2_url || m.original_url || ''}
                    alt=""
                    className="w-full aspect-video object-cover"
                    loading="lazy"
                  />
                )}
              </div>
            ))}
          </div>
        )}

        {/* Footer: engagement + view original */}
        <div className="flex items-center justify-between pt-2" style={{ borderTop: `1px solid ${footerBorder}` }}>
          <EngagementMetrics
            likes={item.likes_count}
            comments={item.comments_count}
            shares={item.shares_count}
            views={item.views_count}
          />
          {item.platform_url && (
            <a
              href={item.platform_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs font-medium transition-colors"
              style={{ color: '#B22234' }}
            >
              View Original <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      </div>
    </article>
  );
}
